/**
 * 2026-02 — Aktionen am Vertragsende: Direkte Kautionsabrechnung erzeugen
 * (auch 0-€-Fall wenn keine Kaution vereinbart war) und Mietverhältnis
 * endgültig schließen (`final_closed`). Endgültiges Schließen ist erst nach
 * fertiger Abrechnung möglich (Backend gibt 409 zurück).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Warning, ShieldCheck } from "@phosphor-icons/react";
import { formatEUR, formatDate } from "@/lib/constants";
import { useConfirm } from "@/components/ConfirmDialog";

export function EndOfTenancyActions({ tenancy, onChanged }) {
  const [settlement, setSettlement] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zeroOpen, setZeroOpen] = useState(false);
  const [zeroForm, setZeroForm] = useState({
    end_of_tenancy_date: tenancy?.return_date || tenancy?.termination_date || tenancy?.contract_end || new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const confirm = useConfirm();
  const deposit = Number(tenancy?.deposit || 0);
  const isClosed = !!tenancy?.final_closed;

  const load = async () => {
    try {
      const { data } = await api.get("/kaution/settlements", { params: { tenancy_id: tenancy.id } });
      setSettlement((data || [])[0] || null);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenancy?.id]);

  const openRegularSettlement = () => {
    // Der bestehende „Zum Kautionskonto"-Flow enthält bereits einen Wizard.
    // Wir scrollen zum Panel und geben eine Info aus.
    document.querySelector('[data-testid="deposit-panel"]')?.scrollIntoView({ behavior: "smooth" });
    toast.info('Bitte im Kautionskonto unten die Abrechnung starten (Button „Kautionsabrechnung").');
  };
  const openZeroSettlement = () => setZeroOpen(true);

  const createZero = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kaution/settlements/zero", {
        tenancy_id: tenancy.id,
        end_of_tenancy_date: zeroForm.end_of_tenancy_date,
        notes: zeroForm.notes || null,
      });
      toast.success("0-€-Abrechnung erstellt");
      setZeroOpen(false);
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const finalClose = async () => {
    const ok = await confirm({
      title: "Mietverhältnis endgültig schließen?",
      description: "Nach dem endgültigen Schließen wird das Mietverhältnis aus der aktiven Liste ausgeblendet. Neue Buchungen sind dann nicht mehr möglich.",
      confirmLabel: "Endgültig schließen",
      danger: false,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancy.id}/final-close`);
      toast.success("Mietverhältnis endgültig geschlossen");
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof d === "object" && d.code === "settlement_missing") {
        toast.error(d.message);
      } else {
        toast.error(typeof d === "string" ? d : "Endabschluss fehlgeschlagen");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="border border-slate-200 rounded-md bg-white p-4" data-testid="end-of-tenancy-actions">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-teal-700" />
          <h3 className="text-sm font-semibold text-slate-900">Vertragsende – Kautionsabrechnung & Endabschluss</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-slate-100 rounded p-3 bg-slate-50/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Kautionsabrechnung</div>
            {settlement ? (
              <div className="text-sm">
                <div className="flex items-center gap-1.5 text-emerald-800">
                  <CheckCircle size={14} />
                  Abrechnung erstellt am {formatDate(settlement.calculation_date)}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Status: {settlement.status} · Nettoauszahlung: {formatEUR(settlement.net_return)}
                  {settlement.zero_amount_reason && <> · <span className="italic">0-€-Fall</span></>}
                </div>
              </div>
            ) : deposit > 0 ? (
              <div className="space-y-2">
                <div className="text-sm text-slate-700">
                  Kaution: <span className="font-semibold tabular-nums">{formatEUR(deposit)}</span> · Abrechnung offen.
                </div>
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 w-full" onClick={openRegularSettlement}
                        data-testid="btn-open-regular-settlement">
                  Kautionsabrechnung erstellen
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-slate-700">Keine Kaution vereinbart — 0-€-Abrechnung zum Abschluss.</div>
                <Button size="sm" variant="outline" className="w-full" onClick={openZeroSettlement}
                        data-testid="btn-open-zero-settlement">
                  0-€-Abrechnung erstellen
                </Button>
              </div>
            )}
          </div>
          <div className="border border-slate-100 rounded p-3 bg-slate-50/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Endabschluss</div>
            {isClosed ? (
              <div className="flex items-center gap-1.5 text-emerald-800 text-sm">
                <CheckCircle size={14} />
                Endgültig geschlossen am {formatDate(tenancy.final_closed_at)}
              </div>
            ) : !settlement ? (
              <div className="flex items-start gap-1.5 text-amber-800 text-xs">
                <Warning size={14} className="shrink-0 mt-0.5" />
                <span>Erst nach fertiger Kautionsabrechnung möglich.</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-slate-600">
                  Mit dem Endabschluss wird das MV aus der Liste ausgeblendet. Es sind dann keine neuen Buchungen mehr möglich.
                </div>
                <Button size="sm" variant="outline" className="w-full" onClick={finalClose} disabled={busy}
                        data-testid="btn-final-close">
                  Mietverhältnis endgültig schließen
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 0-€-Dialog */}
      <Dialog open={zeroOpen} onOpenChange={setZeroOpen}>
        <DialogContent className="max-w-md" data-testid="zero-settlement-dialog">
          <DialogHeader>
            <DialogTitle>0-€-Kautionsabrechnung</DialogTitle>
            <DialogDescription>
              Bestätige die Erstellung einer 0-€-Abrechnung. Für dieses Mietverhältnis
              wurde keine Kaution vereinbart und keine Zahlung erfasst.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium text-slate-600">Vertragsende</label>
              <Input type="date" value={zeroForm.end_of_tenancy_date}
                     onChange={(e) => setZeroForm({ ...zeroForm, end_of_tenancy_date: e.target.value })}
                     data-testid="zero-settlement-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Bemerkung (optional)</label>
              <Textarea rows={2} value={zeroForm.notes}
                        onChange={(e) => setZeroForm({ ...zeroForm, notes: e.target.value })}
                        placeholder="z. B. Keine Kaution vereinbart" data-testid="zero-settlement-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZeroOpen(false)}>Abbrechen</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" disabled={busy} onClick={createZero}
                    data-testid="zero-settlement-confirm">
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EndOfTenancyActions;
